import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { completeOpenHumansConnect } from "@/lib/openhumans.functions";

export const Route = createFileRoute("/_authenticated/openhumans/callback")({
  head: () => ({
    meta: [{ title: "Finishing Open Humans connection — Understand Your Genome" }],
  }),
  component: OpenHumansCallbackPage,
});

/**
 * The OAuth callback (registered Redirect URL /openhumans/callback). Open
 * Humans sends the member back here with `?code=...&state=...` (or
 * `?error=...&error_description=...` if the connection failed on their side).
 *
 * This route sits under the signed-in layout on purpose: completing the
 * connect ties the one-time code to the authenticated user (the CSRF `state`
 * is checked against that same user server-side), so a valid session is a
 * requirement, not an optional convenience. If a session were to expire during
 * the Open Humans round-trip (rare; the token is long-lived and self-refreshes),
 * the layout's guard sends the member to sign in and they simply re-Connect --
 * the safe outcome, since a one-time code can't safely be "completed" for a
 * user we can't authenticate in the same request.
 *
 * The `code` is read from the URL once and never logged or re-sent anywhere but
 * the server function that exchanges it.
 */
function OpenHumansCallbackPage() {
  const navigate = useNavigate();
  const complete = useServerFn(completeOpenHumansConnect);
  const [state, setState] = useState<"processing" | "done" | "error">("processing");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const ohState = params.get("state");
      const ohError = params.get("error");
      const ohErrorDesc = params.get("error_description");

      // Open Humans came back without a usable code (member cancelled, or an
      // OAuth error) -- there is nothing to exchange.
      if (ohError || !code || !ohState) {
        setError(
          ohErrorDesc ??
            ohError ??
            "The connection didn't complete. Please go back and try connecting again.",
        );
        setState("error");
        return;
      }

      try {
        await complete({ data: { code, state: ohState } });
        if (cancelled) return;
        setState("done");
        navigate({ to: "/openhumans" });
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "We couldn't finish connecting to Open Humans. Please try again.",
        );
        setState("error");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [complete, navigate]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-md flex-1 px-5 py-20 text-center">
        {state === "processing" && (
          <>
            <h1 className="text-2xl">Connecting to Open Humans…</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">One moment.</p>
          </>
        )}
        {state === "done" && (
          <>
            <h1 className="text-2xl">Connected</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Taking you to your Open Humans page…
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="text-2xl">We couldn't finish that</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{error}</p>
            <Button asChild className="mt-6">
              <Link to="/openhumans">Back to Open Humans</Link>
            </Button>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
